import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const SCORE_OPTIONS = [1, 2, 3, 4];

const ScoreCell = ({ value, onChange }) => (
  <select className="w-12 h-7 text-xs text-center border rounded" value={value || ""}
    onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}>
    <option value="">-</option>
    {SCORE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
  </select>
);

export const AssessmentEntryRow = ({ entry, studentName, onSave, onDelete, subjectOptions }) => {
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});

  const startEdit = () => {
    setEdits({
      participation_level: entry.participation_level,
      engagement: entry.engagement,
      communication: entry.communication,
      behavior: entry.behavior,
      attention: entry.attention,
      work_completion: entry.work_completion,
      notes: entry.notes || "",
      did_not_attend: entry.did_not_attend || false,
      block: entry.block || "",
      class_name: entry.class_name || "",
    });
    setEditing(true);
  };

  const saveEdit = () => {
    onSave(entry.id, edits);
    setEditing(false);
  };

  const cellColor = (v) => !v ? "" : v >= 3 ? "text-green-700 font-semibold" : v === 2 ? "text-yellow-700" : "text-red-700 font-semibold";

  if (editing) {
    return (
      <tr className="bg-blue-50 border-b" data-testid={`edit-row-${entry.id}`}>
        <td className="px-3 py-1.5 font-medium">{studentName}</td>
        <td className="px-2 py-1.5 text-xs">
          <select className="w-full h-7 text-xs border rounded px-1" value={edits.class_name}
            onChange={e => setEdits(p => ({...p, class_name: e.target.value}))}>
            {(subjectOptions && subjectOptions.length > 0 ? subjectOptions : ["English","Math","Science","Other"]).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </td>
        <td className="px-2 py-1.5 text-xs">
          <select className="w-full h-7 text-xs border rounded px-1" value={edits.block}
            onChange={e => setEdits(p => ({...p, block: e.target.value}))}>
            {["Block 1","Block 2","Block 3","Block 4","Block 5","Block 6"].map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </td>
        <td className="px-1 py-1.5 text-center"><ScoreCell value={edits.participation_level} onChange={v => setEdits(p => ({...p, participation_level: v}))} /></td>
        <td className="px-1 py-1.5 text-center"><ScoreCell value={edits.engagement} onChange={v => setEdits(p => ({...p, engagement: v}))} /></td>
        <td className="px-1 py-1.5 text-center"><ScoreCell value={edits.communication} onChange={v => setEdits(p => ({...p, communication: v}))} /></td>
        <td className="px-1 py-1.5 text-center"><ScoreCell value={edits.behavior} onChange={v => setEdits(p => ({...p, behavior: v}))} /></td>
        <td className="px-1 py-1.5 text-center"><ScoreCell value={edits.attention} onChange={v => setEdits(p => ({...p, attention: v}))} /></td>
        <td className="px-1 py-1.5 text-center"><ScoreCell value={edits.work_completion} onChange={v => setEdits(p => ({...p, work_completion: v}))} /></td>
        <td className="px-2 py-1.5 text-center text-xs">{edits.did_not_attend ? "Y" : ""}</td>
        <td className="px-2 py-1.5"><Input className="h-7 text-xs" value={edits.notes} onChange={e => setEdits(p => ({...p, notes: e.target.value}))} /></td>
        <td className="px-2 py-1.5 text-center">
          <div className="flex gap-1">
            <Button size="sm" className="h-6 px-2 text-[10px] bg-green-600 hover:bg-green-700" onClick={saveEdit}>Save</Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setEditing(false)}>X</Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b hover:bg-gray-50 cursor-pointer ${entry.did_not_attend ? "bg-orange-50/50" : ""}`}
      data-testid={`entry-row-${entry.id}`}>
      <td className="px-3 py-1.5 font-medium text-xs">{studentName}</td>
      <td className="px-2 py-1.5 text-xs">{entry.class_name}</td>
      <td className="px-2 py-1.5 text-xs">{entry.block || entry.group_type}</td>
      <td className={`px-2 py-1.5 text-center text-xs ${cellColor(entry.participation_level)}`}>{entry.participation_level || "-"}</td>
      <td className={`px-2 py-1.5 text-center text-xs ${cellColor(entry.engagement)}`}>{entry.engagement || "-"}</td>
      <td className={`px-2 py-1.5 text-center text-xs ${cellColor(entry.communication)}`}>{entry.communication || "-"}</td>
      <td className={`px-2 py-1.5 text-center text-xs ${cellColor(entry.behavior)}`}>{entry.behavior || "-"}</td>
      <td className={`px-2 py-1.5 text-center text-xs ${cellColor(entry.attention)}`}>{entry.attention || "-"}</td>
      <td className={`px-2 py-1.5 text-center text-xs ${cellColor(entry.work_completion)}`}>{entry.work_completion || "-"}</td>
      <td className="px-2 py-1.5 text-center text-xs">{entry.did_not_attend ? "Y" : ""}</td>
      <td className="px-2 py-1.5 text-xs truncate max-w-[120px]">{entry.notes || ""}</td>
      <td className="px-2 py-1.5 text-center">
        <div className="flex gap-1 justify-center">
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={startEdit}
            data-testid={`edit-btn-${entry.id}`}>Edit</Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-red-600 hover:text-red-800 hover:bg-red-50"
            data-testid={`delete-btn-${entry.id}`}
            onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this assessment entry?")) onDelete(entry.id); }}>Del</Button>
        </div>
      </td>
    </tr>
  );
};
